import { useEffect, useMemo, useRef, useState } from 'react';
import type { MeResponse } from '@palaccount/contracts';
import { AUTH_MESSAGES } from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { resetPermissions } from '@/lib/permissions';
import { exitViewAs, useViewAs, type ViewAsTarget } from '@/lib/view-as';

export const ME_QUERY_KEY = ['me'] as const;

/**
 * Whether a session was ever seen in this browser. Read once when a protected
 * route bounces to sign-in, to tell "session expired" (show the message the
 * spec proposes in GEN-S1 §3) apart from "never signed in" (show nothing).
 */
const HAD_SESSION_KEY = 'app.hadSession';
/**
 * Unlike HAD_SESSION_KEY this one is never consumed or cleared: it only
 * says "someone has signed in from this browser before", so the sign-in
 * screen can greet a returning person without claiming a first-timer back.
 */
const RETURNING_KEY = 'app.returning';

export function isReturningVisitor(): boolean {
  try {
    return localStorage.getItem(RETURNING_KEY) === '1';
  } catch {
    return false;
  }
}

export function markSessionSeen(): void {
  try {
    localStorage.setItem(HAD_SESSION_KEY, '1');
    localStorage.setItem(RETURNING_KEY, '1');
  } catch {
    /* without storage the expiry message is simply never shown */
  }
}

export function consumeSessionEnded(): boolean {
  try {
    const had = localStorage.getItem(HAD_SESSION_KEY) === '1';
    localStorage.removeItem(HAD_SESSION_KEY);
    return had;
  } catch {
    return false;
  }
}

/**
 * The session bootstrap: GET /api/me through the typed client. `null` means
 * "no session" — an expected state, not an error, so it is data rather than a
 * thrown exception (a thrown 401 would put every signed-out visit in the
 * query's error/retry machinery for nothing).
 */
export function useMe() {
  // G-08 "View as": while the Owner previews another person, every reader of
  // the session sees THAT person's permission list and no Owner flag — the
  // menu, the gates, the admin tabs, the user card. Only the projection
  // changes: the cached /api/me row, the cookie and every request stay the
  // Owner's own (lib/view-as.ts). Identity (name, email) is deliberately not
  // swapped — the banner says who is being previewed; the card still says who
  // is signed in.
  const viewAs = useViewAs();
  const select = useMemo(
    () => (viewAs === null ? undefined : (me: MeResponse | null) => previewOf(me, viewAs)),
    [viewAs],
  );
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: async (): Promise<MeResponse | null> => {
      const response = await api.auth.me();
      if (response.status === 200) {
        markSessionSeen();
        return response.body;
      }
      return null;
    },
    staleTime: 30_000,
    retry: false,
    ...(select === undefined ? {} : { select }),
  });
}

function previewOf(me: MeResponse | null, viewAs: ViewAsTarget): MeResponse | null {
  if (me === null) return null;
  return {
    ...me,
    user: { ...me.user, isOwner: false },
    permissions: viewAs.permissions as MeResponse['permissions'],
  };
}

/**
 * THE COMPANY'S TODAY, in the browser (N-16 / EC-19-65).
 *
 * `interface.default_date` says a new document opens on "today", and until
 * this existed each editor answered that with `new Date()` — the reader's own
 * machine, which is neither the company's time zone nor necessarily a clock
 * anybody has set correctly. It was the last place a user's laptop decided a
 * business date.
 *
 * So the answer comes from the API, resolved by CompanyClock in the tenant's
 * own `fiscal.timezone`, and it is used AS IS. Deliberately no arithmetic
 * here: any attempt to roll the date forward locally would have to trust the
 * very clock this exists to stop trusting. A tab left open across the
 * company's midnight keeps yesterday's date until /api/me is refetched, which
 * react-query does on focus — and yesterday from the company's clock is a
 * smaller wrong than any day at all from the reader's.
 *
 * Falls back to the browser's date only while the session has not loaded —
 * which no editor ever renders through, because RequireAuth holds the shell
 * until it has, and useDocumentDate below adopts the real answer regardless.
 */
export function useCompanyToday(): string {
  const { data: me } = useMe();
  return me?.clock.today ?? new Date().toISOString().slice(0, 10);
}

/**
 * THE COMPANY'S MONEY, in the browser (GEN-107).
 *
 * The same shape of answer as `useCompanyToday` above, to the same shape of
 * question, and missing for the same reason. `interface.default_date` let
 * every editor answer "today" with `new Date()`; nothing told the browser
 * what a figure was DENOMINATED IN, so sixty-odd `<Amount currency="USD">`
 * props answered it with a literal — POS, the inventory and sales dashboards,
 * the four report screens, the receipt/delivery/adjustment lists. Every one
 * of those figures is a ledger or stock valuation, which GEN-107 holds in the
 * base currency; the literal printed a dollar sign beside a right number for
 * every company that does not report in dollars.
 *
 * Used AS IS, and deliberately no conversion here or anywhere downstream:
 * this NAMES the money a figure is already in. GEN-115's disclosure belongs
 * to a screen that converted, and GEN-116 hides it when none did — which is
 * every caller of this hook. The banking overview and the financial and
 * cash-flow dashboards keep reading `baseCurrency` off their OWN responses,
 * because those screens consolidate and the figure they state is a property
 * of the answer, not of the session.
 *
 * A screen showing a DOCUMENT's money reads the document (GEN-108) — an
 * invoice raised in euros says euros whatever the company reports in — and
 * must not use this.
 *
 * The 'USD' fallback covers only the moment before the session resolves, and
 * no shell renders through it: RequireAuth holds the app until /api/me
 * answers. It is the shipped default (BR-FIN-05-06) and not a guess.
 */
export function useBaseCurrency(): string {
  const { data: me } = useMe();
  return me?.baseCurrency ?? 'USD';
}

/**
 * A document date field that OPENS on the company's today.
 *
 * A plain `useState(useCompanyToday())` would be right only if the session
 * were always loaded before the editor mounts — which RequireAuth makes true
 * in the app and not true anywhere else. So the seed is adopted when it
 * arrives, and only while the user has not typed over it and no existing
 * document has been loaded into the field. After that the value is theirs.
 */
export function useDocumentDate(): [string, (value: string) => void] {
  const today = useCompanyToday();
  const [value, setValue] = useState(today);
  const seed = useRef(today);
  useEffect(() => {
    if (seed.current !== today && value === seed.current) {
      seed.current = today;
      setValue(today);
    }
  }, [today, value]);
  return [value, setValue];
}

export function useSignOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.auth.signOut({ body: {} });
    },
    onSettled: () => {
      // Whatever the server said, this browser is now signed out: drop every
      // cached read so nothing tenant-scoped survives into the next session.
      try {
        localStorage.removeItem(HAD_SESSION_KEY);
      } catch {
        /* nothing to clear */
      }
      queryClient.clear();
      // The 403-derived visibility set belongs to the session that earned it.
      resetPermissions();
      // And so does a G-08 preview — it must not greet the next sign-in.
      exitViewAs();
    },
  });
}

/**
 * Maps the fixed server sentences (AUTH_MESSAGES — wording that is part of the
 * contract) onto i18n keys so Arabic users get Arabic errors. An unknown
 * message passes through untranslated rather than being swallowed: the
 * lockout/throttle wording must reach the user even if this map ages.
 */
const SERVER_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  [AUTH_MESSAGES.badCredentials]: 'auth.errBadCredentials',
  [AUTH_MESSAGES.lockedOut]: 'auth.errLockedOut',
  [AUTH_MESSAGES.accountDisabled]: 'auth.errDisabled',
  [AUTH_MESSAGES.emailTaken]: 'auth.errEmailTaken',
  // R21 — a lapsed subscription, refused at sign-in. Translated like every
  // other refusal, and it says nothing about money: see the comment on
  // AUTH_MESSAGES.subscriptionExpired for what the wording deliberately omits.
  [AUTH_MESSAGES.subscriptionExpired]: 'auth.errSubExpired',
  // GEN-S1 recovery + BR-GEN-S2-08's activation link. The reset and
  // verification pages read these off a 410, and the wording is the
  // contract's, imported rather than retyped.
  [AUTH_MESSAGES.resetLinkInvalid]: 'reset.errInvalid',
  [AUTH_MESSAGES.resetRateLimited]: 'reset.rateLimited',
  [AUTH_MESSAGES.verificationLinkInvalid]: 'verify.bad',
};

export function serverMessageKey(message: string): string | undefined {
  return SERVER_MESSAGE_KEYS[message];
}
